import { useEffect, useState } from "react";
import { getWeather } from "@/lib/weather";
import {
  Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain,
  CloudSnow, CloudLightning, Wind, Gauge, Droplets, Loader2,
} from "lucide-react";

const iconMap = {
  sun: Sun, cloud: Cloud, "cloud-sun": CloudSun, "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle, "cloud-rain": CloudRain,
  "cloud-snow": CloudSnow, "cloud-lightning": CloudLightning,
};

const WIcon = ({ name, className }) => {
  const C = iconMap[name] || Cloud;
  return <C className={className} />;
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { weekday: "short" });
};

export default function WeatherWidget({ lat, lon, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getWeather(lat, lon)
      .then((d) => active && setData(d))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [lat, lon]);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="weather-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Météo…
      </div>
    );

  if (error || !data)
    return <div className="text-sm text-muted-foreground" data-testid="weather-error">Météo indisponible</div>;

  if (compact)
    return (
      <div className="flex items-center gap-2" data-testid="weather-compact">
        <WIcon name={data.condition.icon} className="w-5 h-5 text-amber-500" />
        <span className="font-mono font-semibold text-emerald-900">{Math.round(data.temperature)}°</span>
        <span className="flex items-center gap-1 text-xs text-emerald-700">
          <Wind className="w-3.5 h-3.5" />{Math.round(data.wind_speed)} km/h
        </span>
      </div>
    );

  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-6" data-testid="weather-widget">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-emerald-800/70">Météo en direct</p>
          <div className="flex items-center gap-3 mt-2">
            <WIcon name={data.condition.icon} className="w-12 h-12 text-amber-500" />
            <div>
              <p className="font-mono text-4xl font-bold text-emerald-950 leading-none">{Math.round(data.temperature)}°C</p>
              <p className="text-sm text-emerald-700 mt-1">{data.condition.label}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-5">
        <div className="rounded-xl bg-emerald-50 p-3 text-center">
          <Wind className="w-4 h-4 mx-auto text-emerald-700" />
          <p className="font-mono font-bold text-emerald-900 mt-1 text-sm">{Math.round(data.wind_speed)}</p>
          <p className="text-[10px] uppercase tracking-wide text-emerald-700/70">km/h vent</p>
        </div>
        <div className="rounded-xl bg-sky-50 p-3 text-center">
          <Gauge className="w-4 h-4 mx-auto text-sky-700" />
          <p className="font-mono font-bold text-sky-900 mt-1 text-sm">{Math.round(data.pressure)}</p>
          <p className="text-[10px] uppercase tracking-wide text-sky-700/70">hPa</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3 text-center">
          <Droplets className="w-4 h-4 mx-auto text-amber-700" />
          <p className="font-mono font-bold text-amber-900 mt-1 text-sm">{Math.round(data.humidity)}</p>
          <p className="text-[10px] uppercase tracking-wide text-amber-700/70">% humidité</p>
        </div>
      </div>

      <div className="flex justify-between mt-5 pt-4 border-t border-emerald-900/10">
        {data.forecast.slice(0, 4).map((f) => (
          <div key={f.date} className="text-center">
            <p className="text-xs font-semibold text-emerald-800 capitalize">{dayLabel(f.date)}</p>
            <WIcon name={f.condition.icon} className="w-6 h-6 mx-auto my-1 text-emerald-600" />
            <p className="font-mono text-xs text-emerald-900">{Math.round(f.t_max)}°<span className="text-emerald-500">/{Math.round(f.t_min)}°</span></p>
          </div>
        ))}
      </div>
    </div>
  );
}
